// Model metadata for the application
export interface ModelMetadata {
  displayName: string;       // User-friendly name to display in UI
  hasVision: boolean;        // Whether the model supports vision capabilities
  scoreMultiplier: number;   // Base multiplier for score calculation
  visionMultiplier: number;  // Multiplier when vision is enabled
  available: boolean;        // Whether the model is currently available for selection
}

// Comprehensive model information
export const MODEL_INFO: Record<string, ModelMetadata> = {
  'gpt-4.1': {
    displayName: 'GPT-4.1',
    hasVision: true,
    scoreMultiplier: 2.37,
    visionMultiplier: 2.54,
    available: true,
  },
  'gpt-4.5-preview': {
    displayName: 'GPT-4.5-preview',
    hasVision: true,
    scoreMultiplier: 3.32,
    visionMultiplier: 3.56,
    available: false,  // Example: temporarily unavailable
  },
  'claude-3-7-sonnet-20250219': {
    displayName: 'Claude 3.7 Sonnet',
    hasVision: true,
    scoreMultiplier: 0.70,
    visionMultiplier: 0.66,
    available: true,
  },
  'gemini/gemini-2.0-flash': {
    displayName: 'Gemini 2.0 Flash',
    hasVision: false,
    scoreMultiplier: 1.33,
    visionMultiplier: 1.33,  // Same as base since vision not supported
    available: true,
  },
};

// Default values for any model not in the list
const DEFAULT_MODEL_INFO: ModelMetadata = {
  displayName: 'AI Model',
  hasVision: false,
  scoreMultiplier: 1.0,
  visionMultiplier: 1.0,
  available: false,
};

// Helper functions to get specific model information

// Get the model's display name
export const getModelDisplayName = (modelId: string): string => {
  return MODEL_INFO[modelId]?.displayName || DEFAULT_MODEL_INFO.displayName;
};

// Check if a model has vision capability
export const hasVisionCapability = (modelId: string): boolean => {
  return MODEL_INFO[modelId]?.hasVision || DEFAULT_MODEL_INFO.hasVision;
};

// Check if a model is available
export const isModelAvailable = (modelId: string): boolean => {
  return MODEL_INFO[modelId]?.available || DEFAULT_MODEL_INFO.available;
};

// Get the score multiplier for a specific model
export const getModelMultiplier = (modelId: string, useVision: boolean = false): number => {
  const model = MODEL_INFO[modelId] || DEFAULT_MODEL_INFO;
  return useVision && model.hasVision ? model.visionMultiplier : model.scoreMultiplier;
};

// Get all model IDs (including unavailable ones)
export const getAllModelIds = (): string[] => {
  return Object.keys(MODEL_INFO);
};

// Get only available model IDs
export const getModelIds = (): string[] => {
  return Object.entries(MODEL_INFO)
    .filter(([_, info]) => info.available)
    .map(([id, _]) => id);
};

// Get available models that support vision
export const getVisionEnabledModels = (): string[] => {
  return Object.entries(MODEL_INFO)
    .filter(([_, info]) => info.hasVision && info.available)
    .map(([id, _]) => id);
};

// Get all available models with their metadata
export const getAvailableModels = (): Record<string, ModelMetadata> => {
  return Object.fromEntries(
    Object.entries(MODEL_INFO).filter(([_, info]) => info.available)
  );
};